import {
  AfterContentInit,
  Component,
  ContentChildren,
  HostBinding,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import {WizardStep} from '../util/wizard-step.interface';
import {WizardState} from '../navigation/wizard-state.model';
import {NavigationMode} from '../navigation/navigation-mode.interface';

/**
 * The `aw-wizard` component defines the root component of a wizard.
 * Through the setting of input parameters for the `aw-wizard` component it's possible to change the location and size
 * of its navigation bar.
 *
 * ### Syntax
 * ```html
 * <aw-wizard [navBarLocation]="location of navigation bar" [navBarLayout]="layout of navigation bar">
 *     ...
 * </aw-wizard>
 * ```
 *
 * ### Example
 *
 * Without completion step:
 *
 * ```html
 * <aw-wizard navBarLocation="top" navBarLayout="small">
 *     <aw-wizard-step>...</aw-wizard-step>
 *     <aw-wizard-step>...</aw-wizard-step>
 * </aw-wizard>
 * ```
 *
 * With completion step:
 *
 * ```html
 * <aw-wizard navBarLocation="top" navBarLayout="small">
 *     <aw-wizard-step>...</aw-wizard-step>
 *     <aw-wizard-step>...</aw-wizard-step>
 *     <aw-wizard-completion-step>...</aw-wizard-completion-step>
 * </aw-wizard>
 * ```
 *
 * @author Marc Arndt
 */
@Component({
  selector: 'aw-wizard',
  template: `<aw-wizard-navigation-bar
  [direction]="navBarDirection"
  *ngIf="navBarLocation == 'top' || navBarLocation == 'left'"
  [ngClass]="{
    vertical: navBarLocation == 'left',
    horizontal: navBarLocation == 'top',
    small: navBarLayout == 'small',
    'large-filled': navBarLayout == 'large-filled',
    'large-filled-symbols': navBarLayout == 'large-filled-symbols',
    'large-empty': navBarLayout == 'large-empty',
    'large-empty-symbols': navBarLayout == 'large-empty-symbols'
  }">
</aw-wizard-navigation-bar>

<div [ngClass]="{
  'wizard-steps': true,
  vertical: navBarLocation == 'left' || navBarLocation == 'right',
  horizontal: navBarLocation == 'top' || navBarLocation == 'bottom'
}">
  <ng-content></ng-content>
</div>

<aw-wizard-navigation-bar
  [direction]="navBarDirection"
  *ngIf="navBarLocation == 'bottom' || navBarLocation == 'right'"
  [ngClass]="{
    vertical: navBarLocation == 'right',
    horizontal: navBarLocation == 'bottom',
    small: navBarLayout == 'small',
    'large-filled': navBarLayout == 'large-filled',
    'large-filled-symbols': navBarLayout == 'large-filled-symbols',
    'large-empty': navBarLayout == 'large-empty',
    'large-empty-symbols': navBarLayout == 'large-empty-symbols'
  }">
</aw-wizard-navigation-bar>
`,
  styles: [`aw-wizard{display:-webkit-box;display:-ms-flexbox;display:flex;-webkit-box-pack:start;-ms-flex-pack:start;justify-content:flex-start}aw-wizard.vertical{-webkit-box-orient:horizontal;-webkit-box-direction:normal;-ms-flex-direction:row;flex-direction:row}aw-wizard.horizontal{-webkit-box-orient:vertical;-webkit-box-direction:normal;-ms-flex-direction:column;flex-direction:column}aw-wizard .wizard-steps{top:0;display:-webkit-box;display:-ms-flexbox;display:flex}aw-wizard .wizard-steps.vertical{min-width:calc(100% - 280px);width:80%;height:100%;-webkit-box-orient:vertical;-webkit-box-direction:normal;-ms-flex-direction:column;flex-direction:column}aw-wizard .wizard-steps.horizontal{width:100%;-webkit-box-orient:horizontal;-webkit-box-direction:normal;-ms-flex-direction:row;flex-direction:row}`],
  encapsulation: ViewEncapsulation.None,
  providers: [WizardState]
})
export class WizardComponent implements OnChanges, AfterContentInit {
  /**
   * A QueryList containing all [[WizardStep]]s inside this wizard
   */
  @ContentChildren(WizardStep)
  public wizardSteps: QueryList<WizardStep>;

  /**
   * The location of the navigation bar inside the wizard.
   * This location can be either top, bottom, left or right
   */
  @Input()
  public navBarLocation = 'top';

  /**
   * The layout of the navigation bar inside the wizard.
   * The layout can be either small, large-filled, large-empty or large-symbols
   */
  @Input()
  public navBarLayout = 'small';

  /**
   * The direction in which the steps inside the navigation bar should be shown.
   * The direction can be either `left-to-right` or `right-to-left`
   */
  @Input()
  public navBarDirection = 'left-to-right';

  /**
   * The navigation mode used for transitioning between different steps.
   * The navigation mode can be either `strict`, `semi-strict` or `free`
   */
  @Input()
  public navigationMode = 'strict';

  /**
   * The initially selected step, represented by its index
   */
  @Input()
  public defaultStepIndex = 0;

  /**
   * True, if the navigation bar shouldn't be used for navigating
   */
  @Input()
  public disableNavigationBar = false;

  /**
   * Returns true if this wizard uses a horizontal orientation.
   * The wizard uses a horizontal orientation, iff the navigation bar is shown at the top or bottom of this wizard
   *
   * @returns True if this wizard uses a horizontal orientation
   */
  @HostBinding('class.horizontal')
  public get horizontalOrientation(): boolean {
    return this.navBarLocation === 'top' || this.navBarLocation === 'bottom';
  }

  /**
   * Returns true if this wizard uses a vertical orientation.
   * The wizard uses a vertical orientation, iff the navigation bar is shown at the left or right of this wizard
   *
   * @returns True if this wizard uses a vertical orientation
   */
  @HostBinding('class.vertical')
  public get verticalOrientation(): boolean {
    return this.navBarLocation === 'left' || this.navBarLocation === 'right';
  }

  /**
   * The navigation mode for this wizard
   */
  public get navigation(): NavigationMode {
    return this.model.navigationMode;
  }

  /**
   * Constructor
   *
   * @param model The model for this wizard component
   */
  constructor(public model: WizardState) {
  }

  /**
   * Updates the model after certain input values have changed
   *
   * @param changes The detected changes
   */
  ngOnChanges(changes: SimpleChanges) {
    for (const propName of Object.keys(changes)) {
      let change = changes[propName];

      if (!change.firstChange) {
        switch (propName) {
          case 'defaultStepIndex':
            this.model.defaultStepIndex = parseInt(change.currentValue, 10);
            break;
          case 'disableNavigationBar':
            this.model.disableNavigationBar = change.currentValue;
            break;
          case 'navigationMode':
            this.model.updateNavigationMode(change.currentValue);
            break;
          /* istanbul ignore next */
          default:
        }
      }
    }
  }

  /**
   * Initialization work
   */
  ngAfterContentInit(): void {
    // add a subscriber to the wizard steps QueryList to listen to changes in the DOM
    this.wizardSteps.changes.subscribe(changedWizardSteps => {
      this.model.updateWizardSteps(changedWizardSteps.toArray());
    });

    // initialize the model
    this.model.disableNavigationBar = this.disableNavigationBar;
    this.model.defaultStepIndex = this.defaultStepIndex;
    this.model.updateWizardSteps(this.wizardSteps.toArray());
    this.model.updateNavigationMode(this.navigationMode);

    // finally reset the whole wizard state
    this.navigation.reset();
  }
}
